import React, { useState, useMemo } from "react";
import { ContentContainer } from "../../../components/layout";
import { Button, LoadingComponent, Select } from "../../../components/core";
import TabComponent from "../../../components/core/tab";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useEffect } from "react";
import {
  DecliningUsageGQL,
  DecliningUsageWithExtrapolationGQL,
  YoYAnalysisGQL,
} from "../../../constants/graphql/operatrions";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import {
  QUERY_CHART_DATA,
  QUERY_LOCKER_NAME,
} from "../../../constants/graphql/drop-off-graphql";
import { MultiSelect } from "primereact/multiselect";
import { Accordion, AccordionTab } from "primereact/accordion";

const DecliningUsageTest = () => {
  // Must match backend DROP_THRESHOLD_PERCENT in declining_usage.py
  const DROP_THRESHOLD_PERCENT = 25;

  // local Variables
  const operatorId = JSON.parse(localStorage.getItem("user")).operator_id;
  const [isLoading, setisLoading] = useState(false);
  const [analysisType, setAnalysisType] = useState("accurate"); // "accurate", "extrapolate", or "yoy"

  // Get current date info for dynamic display
  const today = new Date();
  const currentDay = today.getDate();
  const currentMonth = today.toLocaleString("default", {
    month: "short",
    year: "numeric",
  });

  // Calculate month ranges for dynamic labels
  const getMonthName = (monthsBack) => {
    const date = new Date();
    date.setMonth(date.getMonth() - monthsBack);
    return date.toLocaleString("default", { month: "short", year: "numeric" });
  };

  // Baseline: 3 months (Apr-Jun for Sept)
  const baselineStart = getMonthName(5); // 5 months back
  const baselineEnd = getMonthName(3); // 3 months back

  // Recent: 2 months (Jul-Aug for Sept, or Jul-Projected Sep)
  const recentStart = getMonthName(2); // 2 months back
  const recentEndHistorical = getMonthName(1); // 1 month back
  const recentEndProjected = currentMonth; // current month (projected)

  const usageQUERY = useQuery(
    analysisType === "yoy"
      ? YoYAnalysisGQL
      : analysisType === "accurate"
        ? DecliningUsageGQL
        : DecliningUsageWithExtrapolationGQL,
  );

  const significantDroppers =
    usageQUERY.data?.significantDropLockers ||
    usageQUERY.data?.significantDropLockersWithExtrapolation ||
    usageQUERY.data?.yoyAnalysisLockers ||
    [];

  console.log("📊 Declining Usage Query State:", {
    loading: usageQUERY.loading,
    error: usageQUERY.error,
    dataExists: !!usageQUERY.data,
    lockersCount: significantDroppers.length,
    analysisType: analysisType,
  });

  const [dontShowChart, setdontShowChart] = useState(false);
  const [activeIndex, setActiveIndex] = useState([]);
  // queued variaballs
  const [selectedRadio, setSelectedRadio] = useState("monthly");
  // Per-locker chart view modes: Map of lockerId -> chartViewMode ("year" or "monthly")
  const [chartViewModePerLocker, setChartViewModePerLocker] = useState({});

  const [chartData, setChartData] = useState([]);

  const [fetchChartData, resultFetchChartData] = useLazyQuery(
    QUERY_CHART_DATA,
    { fetchPolicy: "no-cache" },
  );

  const [fetchLockerName, resultFetchLockerName] =
    useLazyQuery(QUERY_LOCKER_NAME);

  // Always show Declining Lockers only
  const [choices] = useState([{ value: 1, name: "Declining Lockers" }]);

  // Dynamic analysis type choices with calculated month ranges
  const analysisTypeChoices = useMemo(
    () => [
      {
        value: "accurate",
        name: `📊 Historical Data (${baselineStart} - ${recentEndHistorical})`,
      },
      {
        value: "extrapolate",
        name: `📈 Projecting (${recentStart} - Projected ${recentEndProjected})`,
      },
      {
        value: "yoy",
        name: "📅 Year-over-Year (This Year vs Last Year)",
      },
    ],
    [baselineStart, recentEndHistorical, recentStart, recentEndProjected],
  );
  const isDecliningLoading = usageQUERY.loading;
  const [selectedLockerName, setSelectedLockerName] = useState([]);
  const [lockerChoices, setLockerChoices] = useState([]);
  const [selectedLockerCluster, setselectedLockerCluster] = useState(1); // Always "Declining Lockers"
  const [selectedClusterIfDeclining, setselectedClusterIfDeclining] = useState(
    [],
  );
  const [decliningLockersFull, setdecliningLockersFull] = useState([]);
  const [isClicked, setIsClicked] = useState(false);

  // Helper: Convert monthly chart data to yearly aggregation for YoY view
  const aggregateMonthlyToYearly = (monthlyData) => {
    const yearlyData = {};
    const monthlyArray = Array.isArray(monthlyData)
      ? monthlyData
      : Object.values(monthlyData || {});

    monthlyArray.forEach((month) => {
      if (month.name) {
        const year = month.name.split(" ")[1]; // Extract year from "Mon YYYY" format
        if (!yearlyData[year]) {
          yearlyData[year] = {
            name: year,
            Claimed: 0,
            Pending: 0,
            Overstay: 0,
            Revoked: 0,
            TakeBack: 0,
            Removed: 0,
          };
        }
        yearlyData[year].Claimed += month.Claimed || 0;
        yearlyData[year].Pending += month.Pending || 0;
        yearlyData[year].Overstay += month.Overstay || 0;
        yearlyData[year].Revoked += month.Revoked || 0;
        yearlyData[year].TakeBack += month.TakeBack || 0;
        yearlyData[year].Removed += month.Removed || 0;
      }
    });

    return Object.values(yearlyData);
  };

  // Get chart view mode for a specific locker
  const getChartViewModeForLocker = (lockerId) => {
    return chartViewModePerLocker[lockerId] || "year"; // Default to "year"
  };

  // Set chart view mode for a specific locker
  const setChartViewModeForLocker = (lockerId, mode) => {
    setChartViewModePerLocker((prev) => ({
      ...prev,
      [lockerId]: mode,
    }));
  };

  // Transform chart data based on view mode (per locker)
  const getChartDataForDisplay = (item, lockerId) => {
    const viewMode = getChartViewModeForLocker(lockerId);
    if (analysisType === "yoy" && viewMode === "year" && Array.isArray(item)) {
      const yearlyData = aggregateMonthlyToYearly(item);
      // Recalculate totals
      const totals = {
        Claimed: 0,
        Pending: 0,
        Overstay: 0,
        Revoked: 0,
        TakeBack: 0,
        Removed: 0,
        Grand: 0,
      };
      yearlyData.forEach((year) => {
        totals.Claimed += year.Claimed;
        totals.Pending += year.Pending;
        totals.Overstay += year.Overstay;
        totals.Revoked += year.Revoked;
        totals.TakeBack += year.TakeBack;
        totals.Removed += year.Removed;
      });
      totals.Grand =
        totals.Claimed +
        totals.Pending +
        totals.Overstay +
        totals.Revoked +
        totals.TakeBack +
        totals.Removed;

      // Create object with transformed data
      const result = yearlyData;
      result.lockerName = item.lockerName;
      result.totals = totals;
      result.lockerId = item.lockerId;
      return result;
    }
    return item;
  };

  async function afterSearch() {
    let lockers;

    // If "Declining Lockers" is selected, fetch the data first
    if (selectedLockerCluster === 1) {
      if (decliningLockersFull.length === 0) {
        try {
          console.log(
            "📊 Fetching declining lockers with analysis type:",
            analysisType,
            "...",
          );
          const result = await usageQUERY.refetch();
          // Handle all three query response structures
          const significantDroppers =
            result.data.significantDropLockers ||
            result.data.significantDropLockersWithExtrapolation ||
            result.data.yoyAnalysisLockers ||
            [];

          const declineLockers = significantDroppers.map((e) => ({
            value: e.lockerId,
            name: e.lockerName,
          }));
          lockers = declineLockers;
          // Also update state for future use
          setselectedClusterIfDeclining(declineLockers);
          setdecliningLockersFull(significantDroppers);
        } catch (e) {
          console.error("Error fetching declining lockers:", e);
          setisLoading(false);
          return;
        }
      } else {
        lockers = selectedClusterIfDeclining;
      }
    } else {
      lockers = lockerChoices;
    }

    // Now fetch chart data with the lockers we have
    performChartDataFetch(lockers);
  }

  function performChartDataFetch(lockers) {
    if (lockers.length > 0) {
      // Extract locker IDs for batch query
      const lockerIds = lockers.map((l) => l.value);

      // Calculate date range - extend to 14+ months for YoY analysis to capture same months from last year
      const dateEnd = new Date().toISOString().split("T")[0];
      const dateStart = new Date();

      // For YoY: go back 14 months to get same months from last year; For others: 5 months
      const monthsBack = analysisType === "yoy" ? 14 : 5;
      dateStart.setMonth(dateStart.getMonth() - monthsBack);
      dateStart.setDate(1);
      const dateStartFormatted = dateStart.toISOString().split("T")[0];

      // Single batch query for all lockers instead of individual queries
      fetchChartData({
        variables: {
          operatorId: operatorId,
          dateStart: dateStartFormatted,
          dateEnd: dateEnd,
          selectedRadio: "monthly",
          selectedLockerName: lockerIds, // Pass ALL locker IDs at once
        },
      })
        .then((response) => {
          // Group response data by locker
          const chartDataByLocker = {};

          // Initialize structure for each locker
          lockers.forEach((locker) => {
            chartDataByLocker[locker.value] = {
              lockerName: locker.name,
              lockerId: locker.value,
              data: [],
              totals: {
                Claimed: 0,
                Pending: 0,
                Overstay: 0,
                Revoked: 0,
                TakeBack: 0,
                Removed: 0,
                Grand: 0,
              },
            };
          });

          // Populate with chart data
          response.data.packageDropoffStatus.forEach((element) => {
            // Note: You'll need to ensure the response includes locker_id field
            // For now, we'll need to update the GraphQL query to include it
            // This is a temporary solution - see comment below
            console.log("Chart data element:", element);
          });

          // TODO: The packageDropoffStatus query needs to include lockerId in the response
          // so we can group data by locker. Update the GraphQL schema to add this field.
          // For now, fall back to individual queries
          performChartDataFetchIndividual(lockers);
        })
        .catch((e) => {
          console.error("Error fetching chart data:", e);
          setisLoading(false);
        });
    } else {
      setisLoading(false);
    }
  }

  function performChartDataFetchIndividual(lockers) {
    // Fallback: individual queries (existing logic)
    if (lockers.length > 0) {
      let tempArr = [];
      let promises = [];

      lockers.forEach((item) => {
        const fetchPromise = fetchChartData({
          variables: buildVariables(item.value),
        })
          .then((r) => {
            var totals = {
              Claimed: 0,
              Pending: 0,
              Overstay: 0,
              Revoked: 0,
              TakeBack: 0,
              Removed: 0,
              Grand: 0,
            };
            var tempData = [];
            r.data.packageDropoffStatus.forEach((element) => {
              console.log({ element });

              // Skip September data if in "accurate" mode (complete months only)
              const currentMonth = new Date().toLocaleString("default", {
                month: "short",
                year: "numeric",
              });
              if (
                analysisType === "accurate" &&
                element.date &&
                element.date.includes(currentMonth)
              ) {
                return;
              }

              totals.Claimed += element.statusClaimed;
              totals.Pending += element.statusPending;
              totals.Overstay += element.statusOverstay;
              totals.Revoked += element.statusRevoked;
              totals.Removed += element.statusRemoved;
              totals.TakeBack += element.statusTakeback;
              totals.Grand += element.statusClaimed;
              totals.Grand += element.statusPending;
              totals.Grand += element.statusOverstay;
              totals.Grand += element.statusRevoked;
              totals.Grand += element.statusRemoved;

              tempData.push({
                name: element.date,
                Claimed: element.statusClaimed,
                Pending: element.statusPending,
                Overstay: element.statusOverstay,
                Revoked: element.statusRevoked,
                TakeBack: element.statusTakeback,
                Removed: element.statusRemoved,
              });
            });

            tempData.lockerName = item.name;
            tempData.totals = totals;
            tempData.lockerId = item.value;
            console.log(tempData.totals);

            tempArr.push(tempData);
          })
          .catch((e) => {
            console.log(e);
          });

        // addpromise to each array
        promises.push(fetchPromise);
      });

      Promise.all(promises)
        .then(() => {
          setChartData(tempArr);
          setisLoading(false);
        })
        .catch((e) => {
          console.log(e);
          setisLoading(false);
        });
    } else {
      setisLoading(false);
    }
  }

  useEffect(() => {
    if (usageQUERY.data) {
      const significantDroppers =
        usageQUERY.data.significantDropLockers ||
        usageQUERY.data.significantDropLockersWithExtrapolation ||
        usageQUERY.data.yoyAnalysisLockers ||
        [];

      console.log("✅ Declining lockers data received:", significantDroppers);
      var tempArr = [];
      significantDroppers.forEach((e) => {
        tempArr.push({
          value: e.lockerId,
          name: e.lockerName,
        });
      });
      setselectedClusterIfDeclining(tempArr);
      setdecliningLockersFull(significantDroppers);
    }
    if (usageQUERY.error) {
      console.error("❌ Error fetching declining lockers:", usageQUERY.error);
    }
  }, [usageQUERY.data, usageQUERY.error]);

  const getSeverityBadge = (declineType, dataQuality) => {
    if (declineType === "EMERGING") {
      return (
        <span className="px-2 py-1 bg-yellow-500 text-white text-xs rounded font-bold">
          EMERGING {dataQuality === "partial" && "⚠️"}
        </span>
      );
    }
    if (declineType === "ESTABLISHED") {
      return (
        <span className="px-2 py-1 bg-orange-500 text-white text-xs rounded font-bold">
          ESTABLISHED
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-red-500 text-white text-xs rounded font-bold">
        ANOMALY
      </span>
    );
  };

  const getYoYCategoryBadge = (category) => {
    const badgeConfig = {
      Worse: { bg: "bg-red-500", icon: "📉" },
      Better: { bg: "bg-green-500", icon: "📈" },
      Stable: { bg: "bg-blue-500", icon: "➡️" },
      Seasonal: { bg: "bg-purple-500", icon: "🔄" },
    };
    const config = badgeConfig[category] || { bg: "bg-gray-500", icon: "❓" };
    return (
      <span
        className={`px-2 py-1 ${config.bg} text-white text-xs rounded font-bold`}
      >
        {config.icon} {category}
      </span>
    );
  };

  const getHealthScoreBadge = (score) => {
    let bgColor = "bg-green-500"; // 75+
    if (score < 25) bgColor = "bg-red-600";
    else if (score < 50) bgColor = "bg-red-500";
    else if (score < 75) bgColor = "bg-yellow-500";

    return (
      <span
        className={`px-2 py-1 ${bgColor} text-white text-xs rounded font-bold`}
      >
        Health: {score.toFixed(1)}
      </span>
    );
  };

  const getPeerComparisonBadge = (underperforming) => {
    if (underperforming) {
      return (
        <span className="px-2 py-1 bg-red-500 text-white text-xs rounded font-bold">
          ⬇️ Volatile Usage
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded font-bold">
        ✓ Stable Usage
      </span>
    );
  };

  const getAccelerationBadge = (isAccelerating, rate) => {
    if (isAccelerating) {
      return (
        <span className="px-2 py-1 bg-red-600 text-white text-xs rounded font-bold">
          📈 Accelerating Decline ({rate.toFixed(1)}%)
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded font-bold">
        Stable Decline
      </span>
    );
  };

  const getLockerSeverity = (lockerId) => {
    const locker = decliningLockersFull.find((l) => l.lockerId === lockerId);
    if (!locker) return null;
    return analysisType === "yoy" ? locker.yoyCategory : locker.declineType;
  };

  const getSeverityRank = (declineType, isYoY) => {
    if (isYoY) {
      if (declineType === "Worse") return 0; // Highest priority
      if (declineType === "Stable") return 1;
      if (declineType === "Seasonal") return 2;
      if (declineType === "Better") return 3;
      return 4;
    }
    if (declineType === "EMERGING") return 0; // Highest priority - needs immediate attention
    if (declineType === "ESTABLISHED") return 1; // Secondary - confirmed trend
    if (declineType === "ANOMALY") return 2; // Lower - irregular pattern
    return 3;
  };

  const sortChartDataBySeverity = () => {
    let sorted = [...chartData];
    sorted.sort((a, b) => {
      const aSeverity = getLockerSeverity(a.lockerId);
      const bSeverity = getLockerSeverity(b.lockerId);
      if (aSeverity === null || bSeverity === null) return 0;
      return (
        getSeverityRank(aSeverity, analysisType === "yoy") -
        getSeverityRank(bSeverity, analysisType === "yoy")
      );
    });
    return sorted;
  };

  useEffect(() => {
    fetchLockerName({
      variables: {
        operatorId: operatorId,
        selectedLockerType: 1, /// TODO: Make it fetch all available selected locker type (loop a loop)
      },
    }).then((r) => {
      console.log(r);
      if (r.data) {
        let tempData = [];
        r.data.lockers.forEach((element, index) => {
          tempData.push({
            value: element.lockerId,
            name: element.lockerName,
          });
        });
        setLockerChoices(tempData);
      }
    });
  }, []);

  const handleSearch = async () => {
    setIsClicked(true);
    await afterSearch();
  };

  function buildVariables(lockerID) {
    const updatedVariables = {
      operatorId: operatorId,
      dateEnd: new Date().toISOString().split("T")[0],
      selectedRadio: selectedRadio,
      selectedLockerName: lockerID,
    };
    const today = new Date();
    // For YoY: go back 14 months to get same months from last year; For others: 5 months
    const monthsBack = analysisType === "yoy" ? 14 : 5;
    today.setMonth(today.getMonth() - monthsBack);
    today.setDate(1);
    const formattedDate = today.toISOString().split("T")[0]; // Format: YYYY-MM-DD

    updatedVariables.dateStart = formattedDate;
    return updatedVariables;
  }

  useEffect(() => {
    if (chartData && chartData.length > 0) {
      // Set activeIndex to be an array of all indexes (0, 1, 2, ..., chartData.length - 1)
      const allIndexes = Array.from(
        { length: chartData.length },
        (_, index) => index,
      );
      setActiveIndex(allIndexes); // Set the active tabs to open
    }
  }, [chartData.length]);

  console.log({ chartData });

  return (
    <ContentContainer containerTitle={"Declining Usage Finder - TESTING"}>
      {/* Analysis Type Selection */}
      <div className="bg-blue-50 border border-blue-300 rounded p-4 mb-4 mx-4">
        <p className="text-sm font-bold text-blue-900 mb-2">
          📊 Analysis Type:
        </p>
        <div className="flex gap-4 flex-col">
          {analysisTypeChoices.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="radio"
                name="analysisType"
                value={option.value}
                checked={analysisType === option.value}
                onChange={(e) => {
                  setAnalysisType(e.target.value);
                  setChartData([]);
                  setdecliningLockersFull([]);
                  setChartViewModePerLocker({}); // Reset per-locker view modes
                }}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">{option.name}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-600 mt-2">
          {analysisType === "accurate"
            ? `✓ Better for analysis: Uses only complete months, excludes partial current month (${currentMonth} 1-${currentDay} excluded)`
            : analysisType === "extrapolate"
              ? `✓ Forward-looking: Projects ${currentMonth} data to full month estimate based on ${currentDay} days of data`
              : `✓ Year-over-Year: Compares this year's ${recentStart}-${recentEndHistorical} period to the same months last year`}
        </p>
      </div>

      {/* Locker Selection - Now Static */}
      <div className="bg-gray-50 border border-gray-300 rounded p-4 mb-4 mx-4">
        <p className="text-sm font-bold text-gray-900">
          📍 Declining Lockers Only
        </p>
        <p className="text-xs text-gray-600 mt-1">
          This tool displays lockers with significant usage drops (≥
          {DROP_THRESHOLD_PERCENT}%)
        </p>
      </div>

      <Button
        value={"SEARCH"}
        disabled={isLoading}
        loading={isLoading}
        onClick={(event) => {
          setisLoading(true);
          handleSearch();
          setChartData([]);
        }}
        className={"w-40 h-8 m-5 bg-yellow-400 hover:bg-yellow-500"}
      />
      {isLoading && <LoadingComponent />}

      {chartData.length > 0 && (
        <>
          <div className="flex items-center w-full justify-center mb-[50px]">
            <p className="text-lg font-bold">
              ** {chartData.length} of {selectedClusterIfDeclining.length}{" "}
              Declining Lockers **
            </p>
          </div>

          <Accordion
            multiple
            activeIndex={activeIndex}
            onTabChange={(e) => setActiveIndex(e.index)}
          >
            {sortChartDataBySeverity().map((item, index) => {
              const severity = getLockerSeverity(item.lockerId);
              const lockerDetail = decliningLockersFull.find(
                (l) => l.lockerId === item.lockerId,
              );
              return (
                <AccordionTab
                  header={
                    <div className="w-full">
                      <div className="flex items-center justify-between pr-2 gap-2">
                        <span className="font-semibold">{item.lockerName}</span>
                        <div className="flex gap-1">
                          {severity !== null &&
                            (analysisType === "yoy"
                              ? getYoYCategoryBadge(severity)
                              : getSeverityBadge(
                                  severity,
                                  lockerDetail?.dataQuality,
                                ))}
                        </div>
                      </div>
                    </div>
                  }
                  key={index}
                >
                  {/* Tab Content */}
                  {!dontShowChart && (
                    <div id={"Dropoff Status History"}>
                      {/* Chart View Mode Selector - Only for YoY Analysis (Per Accordion) */}
                      {analysisType === "yoy" && (
                        <div
                          className={"grid grid-row-3 bg-purple-300 p-2 mb-4"}
                        >
                          <div>
                            <TabComponent
                              tabs={[
                                {
                                  value: "year",
                                  title: "📅 Yearly Comparison",
                                },
                                {
                                  value: "monthly",
                                  title: "📊 Monthly Detail",
                                },
                              ]}
                              selectedRadio={getChartViewModeForLocker(
                                item.lockerId,
                              )}
                              onTabClick={(tabValue) =>
                                setChartViewModeForLocker(
                                  item.lockerId,
                                  tabValue,
                                )
                              }
                            />
                          </div>
                        </div>
                      )}
                      <div id={"chart-container"}>
                        {/* Legend for Decline Types */}
                        <div className="bg-gray-50 border border-gray-300 rounded p-4 mb-4">
                          <p className="text-sm font-bold text-gray-800 mb-2">
                            📖 Analysis Legend:
                          </p>
                          {analysisType !== "yoy" ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div className="flex items-start gap-2">
                                <span className="px-2 py-1 bg-yellow-500 text-white text-xs rounded font-bold whitespace-nowrap">
                                  EMERGING ⚠️
                                </span>
                                <span className="text-xs text-gray-700">
                                  New decline detected in the current month.
                                  Requires immediate monitoring.
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="px-2 py-1 bg-orange-500 text-white text-xs rounded font-bold whitespace-nowrap">
                                  ESTABLISHED
                                </span>
                                <span className="text-xs text-gray-700">
                                  Consistent decline over multiple months.
                                  Indicates a serious, sustained issue.
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="px-2 py-1 bg-red-500 text-white text-xs rounded font-bold whitespace-nowrap">
                                  ANOMALY
                                </span>
                                <span className="text-xs text-gray-700">
                                  Irregular decline pattern. May be temporary or
                                  seasonal fluctuation.
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="flex items-start gap-2">
                                <span className="px-2 py-1 bg-red-500 text-white text-xs rounded font-bold whitespace-nowrap">
                                  📉 Worse
                                </span>
                                <span className="text-xs text-gray-700">
                                  Down ≥25% year-over-year. Urgent attention
                                  needed.
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="px-2 py-1 bg-green-500 text-white text-xs rounded font-bold whitespace-nowrap">
                                  📈 Better
                                </span>
                                <span className="text-xs text-gray-700">
                                  Up ≥10% year-over-year. Good performance.
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded font-bold whitespace-nowrap">
                                  ➡️ Stable
                                </span>
                                <span className="text-xs text-gray-700">
                                  Within ±5% of last year. Consistent usage.
                                </span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded font-bold whitespace-nowrap">
                                  🔄 Seasonal
                                </span>
                                <span className="text-xs text-gray-700">
                                  Likely seasonal pattern. Compare different
                                  times of year.
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className={"grid grid-row-3 bg-yellow-400 p-2"}>
                          <div className={"text-center"}>
                            <span>
                              <h1 className={"font-bold"}>{item.lockerName}</h1>
                            </span>
                          </div>
                        </div>
                        {lockerDetail && (
                          <div className="space-y-4 p-4">
                            {analysisType !== "yoy" ? (
                              <>
                                {/* TRENDS MODE: Main Decline Analysis */}
                                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-sm">
                                  <p className="text-sm text-gray-900 font-medium">
                                    <strong>Why this is declining:</strong>{" "}
                                    {lockerDetail.declineType === "EMERGING"
                                      ? `This locker averaged ${lockerDetail.baselineAvg} packages per month during ${lockerDetail.baselinePeriod}, but usage has dropped to ${lockerDetail.recentAvg} packages per month in ${lockerDetail.recentPeriod} — a loss of ${lockerDetail.dropPackage} packages (${lockerDetail.dropPercentage.toFixed(1)}% decline). This emerging trend just started and requires immediate attention. ${lockerDetail.dataQuality === "partial" ? "⚠️ IMPORTANT: The current month is only partially complete, so this average may change significantly once the month ends." : ""}`
                                      : lockerDetail.declineType ===
                                          "ESTABLISHED"
                                        ? `This locker used to average ${lockerDetail.baselineAvg} packages per month during ${lockerDetail.baselinePeriod}, but has consistently dropped to ${lockerDetail.recentAvg} packages per month in ${lockerDetail.recentPeriod} — losing ${lockerDetail.dropPackage} packages (${lockerDetail.dropPercentage.toFixed(1)}% decline). This is a sustained decline over multiple months, indicating a serious shift in usage patterns.`
                                        : `This locker averaged ${lockerDetail.baselineAvg} packages per month during ${lockerDetail.baselinePeriod}, but shows a significant drop to ${lockerDetail.recentAvg} packages per month in ${lockerDetail.recentPeriod} — ${lockerDetail.dropPackage} fewer packages (${lockerDetail.dropPercentage.toFixed(1)}% decline). However, the decline is irregular across recent months rather than consistent.`}
                                  </p>
                                </div>

                                {/* Priority Ranking */}
                                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-sm">
                                  <p className="text-sm text-gray-900 font-medium">
                                    <strong>🎯 Priority & Action Items:</strong>
                                    <br />
                                    {lockerDetail.isAccelerating &&
                                      "1. URGENT: Acceleration detected - investigate root cause immediately\n"}
                                    • Usage Stability:{" "}
                                    {lockerDetail.volatilityIndex > 30
                                      ? "Highly volatile - may indicate changes in customer buying behavior"
                                      : "Relatively stable pattern"}
                                    <br />• Focus on: Understand why packages
                                    dropped{" "}
                                    {lockerDetail.dropPercentage.toFixed(1)}%
                                    and implement recovery plan
                                  </p>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* YOY MODE: Year-over-Year Analysis */}
                                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-sm">
                                  <p className="text-sm text-gray-900 font-medium">
                                    <strong>Year-over-Year Change:</strong>
                                    <br />
                                    {lockerDetail.yoyCategory === "Worse"
                                      ? `This locker is performing worse this year. In ${lockerDetail.yoyPeriod} last year, it averaged ${lockerDetail.yoyAvg} packages/month, but this year it's averaging ${lockerDetail.recentAvg} packages/month — a decline of ${lockerDetail.yoyChangePercentage.toFixed(1)}%. ${lockerDetail.declineStartMonth ? `The decline started around ${lockerDetail.declineStartMonth}. ` : ""}This represents a concerning shift in year-over-year performance.`
                                      : lockerDetail.yoyCategory === "Better"
                                        ? `This locker is performing better this year! In ${lockerDetail.yoyPeriod} last year, it averaged ${lockerDetail.yoyAvg} packages/month, but this year it's up to ${lockerDetail.recentAvg} packages/month — an improvement of ${lockerDetail.yoyChangePercentage.toFixed(1)}%. This is positive growth you want to maintain.`
                                        : lockerDetail.yoyCategory ===
                                            "Seasonal"
                                          ? `This locker shows seasonal patterns. Comparing ${lockerDetail.yoyPeriod} this year (${lockerDetail.recentAvg} pkg/mo) vs last year (${lockerDetail.yoyAvg} pkg/mo), the difference of ${lockerDetail.yoyChangePercentage.toFixed(1)}% appears to be within expected seasonal variation. Consider comparing different seasons before taking action.`
                                          : `This locker is stable year-over-year. In ${lockerDetail.yoyPeriod} last year, it averaged ${lockerDetail.yoyAvg} packages/month, and this year it's averaging ${lockerDetail.recentAvg} packages/month — a change of only ${lockerDetail.yoyChangePercentage.toFixed(1)}%. Performance is consistent.`}
                                  </p>
                                </div>

                                {/* YoY Category Badge */}
                                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-sm">
                                  <p className="text-sm text-gray-900 font-medium">
                                    <strong>Category: </strong>
                                    {getYoYCategoryBadge(
                                      lockerDetail.yoyCategory,
                                    )}
                                    <br />
                                    <span className="text-xs text-gray-700 mt-2 block">
                                      {lockerDetail.yoyCategory === "Worse"
                                        ? "⚠️ This locker needs attention - usage is down compared to last year."
                                        : lockerDetail.yoyCategory === "Better"
                                          ? "✅ This locker is thriving - usage has improved year-over-year."
                                          : lockerDetail.yoyCategory ===
                                              "Seasonal"
                                            ? "🔄 This appears to be a seasonal fluctuation - check the same period from earlier years for context."
                                            : "➡️ This locker is performing consistently - no major changes from last year."}
                                    </span>
                                  </p>
                                </div>

                                {lockerDetail.declineStartMonth && (
                                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-sm">
                                    <p className="text-sm text-gray-900 font-medium">
                                      <strong>📅 Decline Timeline:</strong>
                                      <br />
                                      The decline appears to have started around{" "}
                                      <strong>
                                        {lockerDetail.declineStartMonth}
                                      </strong>
                                      . Consider what changed operationally
                                      during this period.
                                    </p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        {(() => {
                          const displayData = getChartDataForDisplay(
                            item,
                            item.lockerId,
                          );
                          return (
                            <ResponsiveContainer width={"100%"} height={500}>
                              <BarChart
                                className="mx-auto"
                                width={900}
                                height={400}
                                data={displayData}
                                margin={{
                                  top: 5,
                                  right: 30,
                                  left: 20,
                                  bottom: 10,
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Legend
                                  layout="vertical"
                                  verticalAlign="top"
                                  formatter={(value) => {
                                    const total =
                                      displayData && displayData.totals
                                        ? (displayData.totals[value] ?? 0)
                                        : 0;
                                    return `${
                                      value === "TakeBack" ? "Take-Back" : value
                                    } Total: ${total}`;
                                  }}
                                />
                                <Bar
                                  dataKey={"Claimed"}
                                  stackId={"a"}
                                  fill="#4CBB17"
                                />
                                <Bar
                                  dataKey={"Pending"}
                                  stackId={"a"}
                                  fill="#FFBF00"
                                />
                                <Bar
                                  dataKey={"Overstay"}
                                  stackId={"a"}
                                  fill="#D36E70"
                                />
                                <Bar
                                  dataKey={"Removed"}
                                  stackId={"a"}
                                  fill="#4E5754"
                                />
                                <Bar
                                  dataKey={"TakeBack"}
                                  stackId={"a"}
                                  fill="#3F888F"
                                />
                                <Bar
                                  dataKey={"Cancelled"}
                                  stackId={"a"}
                                  fill="#AEA04B"
                                />
                                <Bar
                                  dataKey={"Revoked"}
                                  stackId={"a"}
                                  fill="#FF5733"
                                />
                                <Bar
                                  dataKey={"Grand"}
                                  stackId={"a"}
                                  fill="#000000"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </AccordionTab>
              );
            })}
          </Accordion>
        </>
      )}
    </ContentContainer>
  );
};

export default DecliningUsageTest;
